import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import { EcranAccueil } from './ecrans/EcranAccueil';
import { EcranResultat } from './ecrans/EcranResultat';
import { EcranScan } from './ecrans/EcranScan';
import { obtenirCle, type ClePubliqueConservee } from './verification/clePublique';
import { verifierSignature } from './verification/signature';
import { decider, uuidDuContenu, type Decision } from './verification/verdict';
import { analyserContenuQr } from '@sygap/core/navigateur';

/**
 * Racine de l'API. Vide en production : la PWA est servie par le même domaine,
 * ce qui évite toute configuration à installer sur un terminal de terrain.
 */
const RACINE_API: string =
  typeof import.meta.env.VITE_SYGAP_API === 'string' ? import.meta.env.VITE_SYGAP_API : '';

type Etape = 'accueil' | 'scan' | 'resultat';

/**
 * SYGAP Verify — contrôle terrain.
 *
 * Trois écrans, pas un de plus : accueil, scan, verdict. L'application n'a ni
 * navigation ni historique — un agent qui contrôle un navire n'explore pas une
 * arborescence, il pointe et il lit.
 *
 * L'obtention de la clé publique est la seule opération qui exige le réseau, et
 * **une seule fois** : ensuite l'application fonctionne entièrement hors ligne.
 */
export function App() {
  const [etape, setEtape] = useState<Etape>('accueil');
  const [cle, setCle] = useState<ClePubliqueConservee | null>(null);
  const [decision, setDecision] = useState<Decision | null>(null);
  const [enCours, setEnCours] = useState(false);
  // Conservée entre les rendus : la clé sert dans un rappel asynchrone.
  const cleCourante = useRef<ClePubliqueConservee | null>(null);

  useEffect(() => {
    let vivant = true;
    void obtenirCle(RACINE_API).then((obtenue) => {
      if (!vivant) return;
      cleCourante.current = obtenue;
      setCle(obtenue);
    });
    return () => {
      vivant = false;
    };
  }, []);

  /**
   * Traite un contenu scanné ou saisi.
   *
   * L'ordre est délibéré : la signature d'abord, **hors ligne**, puis le serveur
   * seulement s'il répond. Un contrôle ne dépend jamais du réseau pour aboutir ;
   * le réseau ne fait qu'ajouter la révocation, que le document ne peut pas
   * porter.
   */
  const traiter = useCallback(async (contenu: string): Promise<void> => {
    const clePublique = cleCourante.current;
    if (clePublique === null) return;

    setEnCours(true);
    try {
      const analyse = analyserContenuQr(contenu);
      const signatureValide =
        analyse !== null &&
        (await verifierSignature(analyse.canonique, analyse.signature, clePublique.clePublique));

      const avisServeur = await interrogerServeur(contenu);

      setDecision(
        decider({
          contenu,
          signatureValide,
          maintenant: new Date(),
          avisServeur,
        }),
      );
      setEtape('resultat');
    } finally {
      setEnCours(false);
    }
  }, []);

  if (etape === 'resultat' && decision !== null) {
    return (
      <EcranResultat
        decision={decision}
        onRecommencer={() => {
          setDecision(null);
          setEtape('accueil');
        }}
      />
    );
  }

  if (etape === 'scan') {
    return (
      <EcranScan
        enCours={enCours}
        onContenu={(contenu) => {
          void traiter(contenu);
        }}
        onAbandon={() => {
          setEtape('accueil');
        }}
      />
    );
  }

  return (
    <EcranAccueil
      cle={cle}
      onScanner={() => {
        setEtape('scan');
      }}
    />
  );
}

/**
 * Demande au serveur si le titre est révoqué.
 *
 * Rend `null` sans délai perceptible quand le réseau manque : c'est le cas
 * nominal en mer, et faire patienter l'agent serait pire que de le prévenir que
 * la révocation n'a pas été contrôlée.
 */
async function interrogerServeur(contenu: string): Promise<{ resultat: string } | null> {
  const uuid = uuidDuContenu(contenu);
  if (uuid === null) return null;

  try {
    const controleur = new AbortController();
    const echeance = globalThis.setTimeout(() => {
      controleur.abort();
    }, 4000);
    const reponse = await fetch(`${RACINE_API}/verify/${uuid}`, {
      signal: controleur.signal,
      headers: { accept: 'application/json' },
    });
    globalThis.clearTimeout(echeance);
    if (!reponse.ok) return null;

    const corps = (await reponse.json()) as { resultat?: unknown };
    return typeof corps.resultat === 'string' ? { resultat: corps.resultat } : null;
  } catch {
    return null;
  }
}
